import { Injectable, Inject, signal, computed, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { WinOlsSolutionsModel } from '../models/WinOlsSolutionsModel';
import { NotificationService } from './Notification.service';
import { MatDialog } from '@angular/material/dialog';
import { DeleteDialogComponent } from '../helpers/delete-dialog/delete-dialog.component';

export enum WinOlsSolutionType {
    OPTIONS_ONLY = 'OPTIONS_ONLY',
    STAGE1_OPTIONS = 'STAGE1_OPTIONS',
    STAGE2_OPTIONS = 'STAGE2_OPTIONS',
    TCU_OPTIONS = 'TCU_OPTIONS',
    TCU_TUNING = 'TCU_TUNING'
}

@Injectable({
    providedIn: 'root'
})
export class WinOlsSolutionsService {

    private url = `${this.API_URL}`;

    public solutions = signal<WinOlsSolutionsModel[]>([]);
    public solutionsIsLoading = signal<boolean>(false);
    public searchQuery = signal('');
    public showInputRow = signal(false);
    public newRow = signal<WinOlsSolutionsModel>({} as WinOlsSolutionsModel);
    public solutionToEditIndex = signal<number | null>(null);
    public isWaitingResponse = signal<boolean>(false);
    public currentSolutionType = signal<WinOlsSolutionType | undefined>(undefined);
    public pageNumber = signal<number>(0);
    public pageSize = signal<number>(50);


    public notificationService = inject(NotificationService);
    private dialog = inject(MatDialog);

    constructor(
        @Inject('API_URL') private API_URL: string,
        private httpClient: HttpClient
    ) {}


    solutionsFiltered = computed(() => {
        const list = this.solutions();

        if (this.searchQuery()?.length >= 1) {
            return list.filter(item =>
                item.tuningName.toLowerCase().includes(this.searchQuery().toLowerCase()) ||
                item.evcCredits.toString().includes(this.searchQuery())
            );
        } else {
            return list;
        }
    });

    initializeForSolutionType(solutionType: WinOlsSolutionType) {
        this.searchQuery.set('');
        this.currentSolutionType.set(solutionType);
        this.resetState();
        this.getWinOlsSolutions();
    }

    resetState() {
        this.showInputRow.set(false);
        this.solutionToEditIndex.set(null);
        this.resetNewRowValue();
    }

    checkDuplicateTuningName(value: string, excludeId?: number): boolean {
        const lowerName = value.trim().toLowerCase();
        return this.solutions().some(
            (solution) => solution.id !== excludeId &&
                solution.tuningName?.trim().toLowerCase() === lowerName
        );
    }

    hasNegativeEvcCredits(solution: WinOlsSolutionsModel): boolean {
        return typeof solution?.evcCredits === 'number' && solution.evcCredits < 0;
    }

    isValidSolution(solution: WinOlsSolutionsModel, excludeId?: number): boolean {
        const tuningName = solution?.tuningName?.trim();
        return !!tuningName &&
            typeof solution.evcCredits === 'number' &&
            Number.isFinite(solution.evcCredits) &&
            solution.evcCredits >= 0 &&
            !this.checkDuplicateTuningName(tuningName, excludeId);
    }


    onRefreshTable(): void {
        const solutionType = this.currentSolutionType();
        if (!solutionType) {
            console.error('Solution type not initialized');
            return;
        }

        this.resetState();
        this.getWinOlsSolutions();
    }

    getWinOlsSolutions() {
        const solutionType = this.currentSolutionType();
        if (!solutionType) {
            console.error('Solution type not initialized');
            return;
        }

        this.solutionsIsLoading.set(true);
        this.getAllWinOlsSolutionsByType(solutionType).subscribe({
            next: (response) => {
                this.solutions.set(response);
                this.solutionsIsLoading.set(false);
            },
            error: (error) => {
                this.solutionsIsLoading.set(false);
                this.notificationService.showErrorMessage('Error loading solutions', 'Error');
            }
        });
    }



    addNewWinOlsSolutionItem(newRow: WinOlsSolutionsModel) {
        if (this.isWaitingResponse() || !this.isValidSolution(newRow)) {
            return;
        }

        this.isWaitingResponse.set(true);
        const payload = { ...newRow, tuningName: newRow.tuningName.trim() };
        this.createWinOlsSolution(this.currentSolutionType(), payload).subscribe({
            next: (response) => {
                this.solutions.update((solutions) => [...solutions, response]);
                this.isWaitingResponse.set(false);
                this.notificationService.showSuccessMessage('Successfully added.', 'Successfully added new solution.');
                this.showInputRow.set(false);
                this.resetNewRowValue();
            },
            error: (error: HttpErrorResponse) => {
                this.isWaitingResponse.set(false);
                this.handleError(error);
            }
        });
    }


    updateWinOlsSolution(solution: WinOlsSolutionsModel, id: number) {
        if (this.isWaitingResponse() || !this.isValidSolution(solution, id)) {
            return;
        }

        this.isWaitingResponse.set(true);
        const payload = { ...solution, tuningName: solution.tuningName.trim() };
        this.updateWinOlsSolutions(this.currentSolutionType(), id, payload).subscribe({
            next: (response) => {
                this.solutions.update((solutions) =>
                    solutions.map((sol) => (sol.id === solution.id) ? response : sol)
                );
                this.isWaitingResponse.set(false);
                this.notificationService.showSuccessMessage("Successfully updated", "Solution updated");
                this.closeEditedItem();
            },
            error: (error) => {
                this.isWaitingResponse.set(false);
                this.notificationService.showErrorMessage("Something went wrong.", "Error!");
            }
        });
    }


    deleteWinOlsSolutionItem(solution: WinOlsSolutionsModel) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: "350px",
            data: "Are you sure you want to delete this solution?"
        });

        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.deleteWinOlsSolutionRequest(solution.id);
            }
        });
    }

    deleteWinOlsSolutionRequest(id: number) {
        this.deleteWinOlsSolution(this.currentSolutionType(), id).subscribe({
            next: () => {
                this.solutions.update((solutions) => solutions.filter((solution) => solution.id !== id));
                this.notificationService.showSuccessMessage('Successfully deleted', `Solution ${id} deleted.`);
            },
            error: () => {
                this.notificationService.showErrorMessage('Something went wrong', 'Error');
            }
        });
    }


    searchSolutions(searchQuery: string) {
        this.searchQuery.set(searchQuery);
    }

    resetNewRowValue() {
        this.newRow.set({} as WinOlsSolutionsModel);
    }

    closeEditedItem() {
        this.solutionToEditIndex.set(null);
    }

    private handleError(error: HttpErrorResponse) {
        if (error.status === 400) {
            this.notificationService.showErrorMessage("Something went wrong!", "Error occurred");
        }
        if (error.error?.errors) {
            const errorMessage = error.error?.errors;
            for (const [key, value] of Object.entries(errorMessage)) {
                this.notificationService.showErrorMessage(value as string, `${key} Error`);
            }
        }
    }

    getWinOlsSolutionById(type: WinOlsSolutionType, id: number): Observable<WinOlsSolutionsModel> {
        return this.httpClient.get<WinOlsSolutionsModel>(`${this.url}/win-ols-solutions/${type}/${id}`);
    }

    updateWinOlsSolutions(type: WinOlsSolutionType, id: number, solution: WinOlsSolutionsModel): Observable<WinOlsSolutionsModel> {
        return this.httpClient.put<WinOlsSolutionsModel>(`${this.url}/win-ols-solutions/${type}/${id}`, solution);
    }

    deleteWinOlsSolution(type: WinOlsSolutionType, id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.url}/win-ols-solutions/${type}/${id}`);
    }

    getAllWinOlsSolutionsByType(type: WinOlsSolutionType): Observable<WinOlsSolutionsModel[]> {
        return this.httpClient.get<WinOlsSolutionsModel[]>(`${this.url}/win-ols-solutions/${type}`);
    }

    createWinOlsSolution(type: WinOlsSolutionType, solution: WinOlsSolutionsModel): Observable<WinOlsSolutionsModel> {
        return this.httpClient.post<WinOlsSolutionsModel>(`${this.url}/win-ols-solutions/${type}`, solution);
    }
}
